import { useState } from "react";
import { toast } from "sonner";
import { formatApiErrorDetail, guardaFicha } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ETAPAS } from "@/lib/etapa.mjs";
import AALogo from "@/components/AALogo";

/**
 * Quién eres y en qué curso estás, para las cuentas que no pasaron por el registro.
 *
 * POR QUE HACE FALTA UNA PANTALLA Y NO BASTA CON EL FORMULARIO. En septiembre la mayoría de
 * las cuentas no las abre nadie: las crea el profesor pegando la lista de correos de su
 * clase, y de ahí sale un alias sacado del correo —«ana.gil»— y ningún curso. Si esto viviera
 * solo en el registro, los ciento ochenta alumnos del piloto serían justamente los que no
 * tendrían ficha, y el mentor le hablaría igual a un chaval de 1.º de la ESO que a uno de
 * 2.º de Bachillerato. Es el fallo de la casa otra vez: construido en el sitio por el que esa
 * gente no pasa.
 *
 * Se corta en `ProtectedRoute`, en el mismo sitio que la contraseña del aula y que la
 * pregunta de la edad, y por lo mismo: así no hay pantalla con sesión que se escape.
 */
export default function PuertaDeFicha() {
  const { user, refresh } = useAuth();
  // El alias del alta —«ana.gil»— no es un nombre: se deja el campo vacío para que lo escriba
  // de verdad, en vez de que lo acepte de un vistazo y se quede así todo el curso.
  const [nombre, setNombre] = useState("");
  const [apellidos, setApellidos] = useState(user?.apellidos || "");
  const [etapa, setEtapa] = useState(user?.etapa || "");
  const [ocupado, setOcupado] = useState(false);

  const campo =
    "w-full px-4 py-3 bg-white border border-alpha-black/15 rounded focus:border-alpha-red outline-none";
  const etiqueta =
    "block font-mono-alpha text-[10px] uppercase tracking-widest text-alpha-black/50 mb-2";

  const envia = async (e) => {
    e.preventDefault();
    setOcupado(true);
    try {
      await guardaFicha(nombre, apellidos, etapa);
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "No ha salido.");
    } finally {
      setOcupado(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] dvh:min-h-[calc(100svh-4rem)] bg-alpha-off flex items-center" data-testid="puerta-ficha">
      <div className="w-full max-w-md mx-auto px-6 py-10">
        <div className="mb-8"><AALogo size={40} lockup="vertical" /></div>
        <h1 className="font-serif-display text-4xl leading-[1] tracking-tight mb-3">
          Dinos quién eres.
        </h1>
        <p className="text-alpha-black/60 mb-8 leading-snug">
          Tu nombre y tus apellidos, para que tu profesor sepa quién eres en clase, y tu
          curso, para que el mentor te hable a tu nivel. En la Liga y en el Ágora no sale tu
          nombre: ahí llevas tu apodo.
        </p>

        <form onSubmit={envia} className="space-y-4">
          <div>
            <label className={etiqueta}>Nombre</label>
            <input className={campo} required value={nombre} autoComplete="given-name"
                   onChange={(e) => setNombre(e.target.value)} data-testid="ficha-nombre" />
          </div>
          <div>
            <label className={etiqueta}>Apellidos</label>
            <input className={campo} required value={apellidos} autoComplete="family-name"
                   onChange={(e) => setApellidos(e.target.value)} data-testid="ficha-apellidos" />
          </div>
          <div>
            <label className={etiqueta}>¿En qué curso estás?</label>
            <select className={campo} required value={etapa}
                    onChange={(e) => setEtapa(e.target.value)} data-testid="ficha-etapa">
              <option value="">Elige tu curso</option>
              {ETAPAS.map((e) => (
                <option key={e.clave} value={e.clave}>{e.nombre}</option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            disabled={ocupado || !nombre.trim() || !apellidos.trim() || !etapa}
            className="w-full px-5 py-4 bg-alpha-red text-white font-mono-alpha uppercase text-[12px] tracking-widest rounded-full disabled:opacity-40"
            data-testid="ficha-guardar"
          >
            Continuar
          </button>
        </form>
      </div>
    </main>
  );
}
